import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import { connect } from "react-redux";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Paper,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LeftSidebar as NavFrame } from "layout-blueprints";
import StandardHeader from "../../components/global/StandardHeader";
import { TYPE_COLOR } from "./outcomes";
import { fromUtc, processedRivalText } from "./DetailParts";

const shortDate = (date) => (date ? moment(date, "YYYY-MM-DD").format("MMM D, YYYY") : "—");
const when = (utc) => {
  const at = fromUtc(utc);
  return at ? at.format("MMM D, YYYY h:mm A") : "—";
};
// Newest first by the chosen date. The date range always filters on the deleted date.
const SORTS = { deleted: "deletedAt", created: "created", moveDate: "moveDate" };
const STATUS_COLOR = { open: "#0b7a5c", processed: "#467a2a", flagged: "#c62828", "on hold": "#9a6400", deleted: "#7b828c" };

// Who deleted a move and why. Deletions before logging started (and the dashboard's own) have no record.
function deletedLine(move) {
  const at = when(move.deletedAt);
  if (!move.logged) {
    return `Deleted ${at} · who deleted it wasn't recorded${move.duringBatchRun ? " (during the 2 AM batch run)" : ""}`;
  }
  const kept = move.otherMoveIns.find((o) => o.kept);
  const why = move.reason === "DUPLICATE"
    ? ` · duplicate${kept ? `, kept ${kept.name || "another move-in"} (${kept.status})` : ""}`
    : move.reason === "STALE" ? " · created more than 2 months ago and never processed" : "";
  return `Deleted ${at} by ${move.deletedBy}${why}`;
}

function OtherMoveIns({ move }) {
  if (move.kind !== "in") return null;
  const rows = [{ ...move, status: "deleted", self: true }, ...move.otherMoveIns];
  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 0.25 }}>Move-ins on this unit</Typography>
      {rows.map((o) => (
        <Box
          key={o.uuid}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr auto", md: "minmax(0, 1.4fr) 0.8fr 0.7fr minmax(0, 1.4fr) 0.7fr" },
            columnGap: 1.5,
            py: 0.5,
            px: 0.75,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: o.self ? "#f6f7f9" : o.kept ? "#eef5ff" : undefined,
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: o.self || o.kept ? 600 : 400 }}>
            {o.self ? o.name || "—" : (
              <Link component={RouterLink} to={`/move-ins-outs/in/${o.uuid}`}>{o.name || "—"}</Link>
            )}
            {o.self ? " · this one" : o.kept ? " · kept" : ""}
            {o.email ? <Box component="span" sx={{ display: "block", fontSize: 11.5, fontWeight: 400, color: "text.secondary" }}>{o.email}</Box> : null}
          </Typography>
          <Typography sx={{ fontSize: 12.5, display: { xs: "none", md: "block" } }}>Moves in {shortDate(o.moveDate)}</Typography>
          <Typography sx={{ fontSize: 12.5, display: { xs: "none", md: "block" } }}>{o.source || "Source unknown"}</Typography>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", display: { xs: "none", md: "block" } }}>
            Created {when(o.created)}{o.createdByName ? ` by ${o.createdByName}` : ""}
          </Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: STATUS_COLOR[o.status] || "text.secondary", textAlign: "right" }}>
            {o.status}{o.accountNum && o.status === "processed" ? ` · #${o.accountNum}` : ""}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function DeletedRow({ move, onRestore }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ borderTop: "1px solid", borderColor: "divider", py: 1.25, px: 1.5 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "34px minmax(0, 1fr) auto auto", columnGap: 1.25, alignItems: "start" }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: TYPE_COLOR[move.kind], pt: 0.25 }}>{move.kind === "in" ? "IN" : "OUT"}</Typography>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            <Link component={RouterLink} to={`/move-ins-outs/${move.kind}/${move.uuid}`} underline="hover" color="inherit">{move.name || "—"}</Link>
            <Box component="span" sx={{ fontWeight: 400, color: "text.secondary" }}>
              {` · ${move.kind === "in" ? "moves in" : "moves out"} ${shortDate(move.moveDate)}`}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
            {[move.propertyName, move.unitAddress].filter(Boolean).join(" · ") || "No unit linked"}
            {move.email ? ` · ${move.email}` : ""}
          </Typography>
          <Typography sx={{ fontSize: 12.5, mt: 0.25, color: move.logged ? "text.primary" : "text.secondary" }}>{deletedLine(move)}</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {move.source || "Source unknown"} · created {when(move.created)}{move.createdByName ? ` by ${move.createdByName}` : ""}
          </Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={() => onRestore(move)} sx={{ textTransform: "none", whiteSpace: "nowrap" }}>Restore…</Button>
        {move.kind === "in" ? (
          <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label={open ? "Hide move-ins on this unit" : "Show move-ins on this unit"}>
            <ExpandMoreIcon fontSize="small" sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
          </IconButton>
        ) : <Box />}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: { xs: 0, sm: "46px" } }}>
          <OtherMoveIns move={move} />
        </Box>
      </Collapse>
    </Box>
  );
}

function DeletedMovesPage(props) {
  const details = props.user?.details;
  const canView = Boolean(details && (details.billing || details.administration || details.isSuperUser));
  // The board's "Deleted since yesterday" link opens this from yesterday; otherwise the last 90 days.
  const linkedStart = new URLSearchParams(useLocation().search).get("startDate");
  const [range, setRange] = useState({
    startDate: moment(linkedStart, "YYYY-MM-DD", true).isValid() ? linkedStart : moment().subtract(90, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("all");
  const [who, setWho] = useState("all");
  const [sortBy, setSortBy] = useState("deleted");
  const [restoring, setRestoring] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null);

  const load = () => {
    setLoading(true);
    setError("");
    return axios.get("/moves-dashboard/deleted", { params: range })
      .then((response) => setData(response.data))
      .catch((err) => setError(err?.response?.data?.message || "Couldn't load deleted moves."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (canView) load(); }, [canView, range.startDate, range.endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const moves = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.moves || []).filter((m) => {
      if (kind !== "all" && m.kind !== kind) return false;
      if (who === "batch" && m.deletedBy !== "The batch") return false;
      if (who === "unknown" && m.logged) return false;
      if (!q) return true;
      return [m.name, m.email, m.propertyName, m.unitAddress].some((v) => (v || "").toLowerCase().includes(q));
    }).sort((a, b) => String(b[SORTS[sortBy]] || "").localeCompare(String(a[SORTS[sortBy]] || "")));
  }, [data, search, kind, who, sortBy]);

  // Open move-ins left on the unit: the batch keeps one per unit, so restoring next to one sets up another deletion.
  const openTwins = restoring && restoring.kind === "in" ? restoring.otherMoveIns.filter((o) => o.status === "open" || o.status === "flagged" || o.status === "on hold") : [];
  // The move kept in this one's place, or with no record of that, the latest processed move-in on the unit.
  const processedRival = restoring && restoring.kind === "in"
    ? restoring.otherMoveIns.find((o) => o.kept && o.status === "processed")
      || [...restoring.otherMoveIns].reverse().find((o) => o.status === "processed" && o.created > restoring.created)
    : null;

  const restore = async () => {
    setSaving(true);
    try {
      await axios.post(`/moves-dashboard/move-${restoring.kind}s/${restoring.uuid}/restore`);
      setSnack({ severity: "success", text: `Restored ${restoring.name || "the move"}.`, uuid: restoring.uuid, kind: restoring.kind });
      setRestoring(null);
      await load();
    } catch (err) {
      setSnack({ severity: "error", text: err?.response?.data?.message || "Couldn't restore the move." });
    } finally {
      setSaving(false);
    }
  };

  if (!details) {
    return (
      <NavFrame page="Moves Dashboard">
        <StandardHeader title="Deleted moves" />
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
      </NavFrame>
    );
  }
  if (!canView) {
    return (
      <NavFrame page="Moves Dashboard">
        <StandardHeader title="Deleted moves" />
        <Divider className="mb-3 mt-3" />
        <h3>You do not have privileges for the moves dashboard</h3>
      </NavFrame>
    );
  }

  return (
    <NavFrame page="Moves Dashboard">
      <StandardHeader title="Deleted moves" />
      <Divider className="mb-3 mt-3" />
      <Link component={RouterLink} to="/move-ins-outs" sx={{ fontSize: 13 }}>← Moves dashboard</Link>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", my: 2 }}>
        <TextField size="small" placeholder="Search name, email, property, unit" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260, flex: "1 1 260px" }} />
        <TextField size="small" type="date" label="Deleted from" InputLabelProps={{ shrink: true }} value={range.startDate} onChange={(e) => e.target.value && setRange((r) => ({ ...r, startDate: e.target.value }))} />
        <TextField size="small" type="date" label="to" InputLabelProps={{ shrink: true }} value={range.endDate} onChange={(e) => e.target.value && setRange((r) => ({ ...r, endDate: e.target.value }))} />
        <ToggleButtonGroup size="small" exclusive value={kind} onChange={(e, v) => v && setKind(v)}>
          <ToggleButton value="all" sx={{ textTransform: "none" }}>All</ToggleButton>
          <ToggleButton value="in" sx={{ textTransform: "none" }}>Move-ins</ToggleButton>
          <ToggleButton value="out" sx={{ textTransform: "none" }}>Move-outs</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={who} onChange={(e, v) => v && setWho(v)}>
          <ToggleButton value="all" sx={{ textTransform: "none" }}>Anyone</ToggleButton>
          <ToggleButton value="batch" sx={{ textTransform: "none" }}>The batch</ToggleButton>
          <ToggleButton value="unknown" sx={{ textTransform: "none" }}>Not recorded</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={sortBy} onChange={(e, v) => v && setSortBy(v)} aria-label="Sort, newest first">
          <ToggleButton value="deleted" sx={{ textTransform: "none" }}>Deleted date</ToggleButton>
          <ToggleButton value="created" sx={{ textTransform: "none" }}>Created date</ToggleButton>
          <ToggleButton value="moveDate" sx={{ textTransform: "none" }}>Move date</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {data?.truncated ? <Alert severity="info" sx={{ mb: 2 }}>Showing the latest 500 of each kind. Narrow the dates to see older ones.</Alert> : null}

      <Paper variant="outlined">
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", px: 1.5, py: 1 }}>
          {loading ? "Loading…" : `${moves.length} deleted move${moves.length === 1 ? "" : "s"}`}
        </Typography>
        {!loading && !moves.length ? (
          <Typography sx={{ fontSize: 13.5, color: "text.secondary", px: 1.5, pb: 2 }}>No deleted moves match.</Typography>
        ) : null}
        {moves.map((m) => <DeletedRow key={`${m.kind}-${m.uuid}`} move={m} onRestore={setRestoring} />)}
      </Paper>

      <Dialog open={Boolean(restoring)} onClose={() => setRestoring(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Restore this move</DialogTitle>
        {restoring ? (
          <DialogContent>
            <Typography>
              Restore {restoring.name || "this"}'s move-{restoring.kind} ({shortDate(restoring.moveDate)})? It goes back on the moves dashboard as an open move.
            </Typography>
            {openTwins.length ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {openTwins.map((o) => o.name || "Another move-in").join(", ")} {openTwins.length === 1 ? "is" : "are"} still open on this unit.
                The batch keeps one open move-in per unit, so its next run deletes one of them again unless you delete the other.
              </Alert>
            ) : null}
            {processedRival ? <Alert severity="warning" sx={{ mt: 2 }}>{processedRivalText(processedRival)}</Alert> : null}
            {restoring.processed ? <Alert severity="warning" sx={{ mt: 2 }}>This move was already processed before it was deleted.</Alert> : null}
          </DialogContent>
        ) : null}
        <DialogActions>
          <Button onClick={() => setRestoring(null)}>Cancel</Button>
          <Button variant="contained" onClick={restore} disabled={saving}>{saving ? "Restoring…" : "Restore"}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={6000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {snack ? (
          <Alert
            severity={snack.severity}
            onClose={() => setSnack(null)}
            action={snack.uuid ? <Button color="inherit" size="small" component={RouterLink} to={`/move-ins-outs/${snack.kind}/${snack.uuid}`}>Open</Button> : null}
          >
            {snack.text}
          </Alert>
        ) : <span />}
      </Snackbar>
    </NavFrame>
  );
}

function mapStateToProps(state) {
  return { user: state.user };
}

export default connect(mapStateToProps)(DeletedMovesPage);
